/* eslint-disable sort-keys-custom-order-fix/sort-keys-custom-order-fix */
import { uniq } from 'ramda';

import { relayStylePaginationV2 } from '@speeki/apollo-utils';

import { LanguageCodeType } from '@@types/CommonTypes';

import { LanguageCode } from '@graphql/generated/graphql.ts';

import { defaultSelectOption, languagesOptions } from '@utils/dictionary';
import { getDialCodeAndPhone } from '@utils/helpers';

import { defaultDataIdFromObject, InMemoryCache } from '@apollo/client';
import { currentLanguageVar } from '@boot/reactiveVariables';
import { GUEST_USER_ID } from '@shared/constants';

export const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                reportCases: relayStylePaginationV2(['input', ['filter']]),
                listNotifications: {
                    read(existing: unknown) {
                        return existing;
                    },
                    merge(existing, incoming, { args }) {
                        const offset = args?.paginationInput?.offset;
                        const isLoadMore = existing && offset && offset !== 1;
                        const list = uniq([
                            ...(existing?.page?.edges ?? []),
                            ...(incoming?.page?.edges ?? []),
                        ]);

                        const mergeData = {
                            page: {
                                edges: [...list],
                                info: {
                                    hasNextPage:
                                        incoming?.page?.info?.hasNextPage,
                                },
                            },
                        };

                        if (isLoadMore) return mergeData;
                        else return incoming;
                    },
                },
            },
        },
        ConversationAssistantResponse: {
            fields: {
                slotOptions: {
                    merge: false,
                },
            },
        },
        UserType: {
            fields: {
                fullName(_, { readField }) {
                    const firstName = readField<string | null>('firstName');
                    const lastName = readField<string | null>('lastName');
                    const name = readField<string | null>('name');
                    const email = readField<string | null>('email');
                    return (
                        `${firstName || ''} ${lastName || ''}`.trim() ||
                        // fallback data for users which doesn't have first and last name
                        name ||
                        email
                    );
                },
                uuid(value) {
                    return (value ?? GUEST_USER_ID) as string;
                },
                language: {
                    merge: (_, language) => {
                        currentLanguageVar(language as LanguageCode);

                        return language as LanguageCode;
                    },
                },
                languageSelect(_, { readField }) {
                    const language = readField<LanguageCodeType>('language');

                    return defaultSelectOption(languagesOptions, language);
                },
                phoneCountryPrefix(_, { readField }) {
                    const phoneNumber = readField<string | null>('phoneNumber');
                    const [countryCode] = getDialCodeAndPhone(phoneNumber);

                    return countryCode;
                },
                phoneNonPrefixed(_, { readField }) {
                    const phoneNumber = readField<string | null>('phoneNumber');
                    const [, phone] = getDialCodeAndPhone(phoneNumber);

                    return phone;
                },
                isPersonalInfoCompleted(_, { readField }) {
                    const firstName = readField('firstName');
                    const lastName = readField('lastName');

                    return !!(firstName && lastName);
                },
            },
        },
    },
    dataIdFromObject: (responseObject) => {
        if (
            responseObject.uuid === null &&
            responseObject.__typename === 'UserType'
        ) {
            return `${responseObject.__typename}:${GUEST_USER_ID}`;
        }

        if (responseObject.uuid && typeof responseObject.uuid === 'string') {
            return `${responseObject.__typename}:${responseObject.uuid}`;
        }

        return defaultDataIdFromObject(responseObject);
    },
});
